import { CommonModule } from "@angular/common";
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  FormControl,
  FormControlStatus,
  FormRecord,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { NgxRerenderModule } from "ngx-rerender";
import { getKey } from "pax-ui/core";
import { Subject, Subscription } from "rxjs";

import { FormFieldComponent } from "../form-field/form-field";
import { FocusChangedEvent } from "../form-field/inputs/input-typeahead";
import {
  FormControlType,
  FormControlValue,
  FormField,
  SimpleDerivationContext,
} from "../form-field/model/base";

@Component({
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    FormFieldComponent,
    NgxRerenderModule,
  ],
  selector: "pax-form-factory",
  templateUrl: "./form-factory.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class FormFactory<T = any>
  implements OnChanges, OnDestroy, AfterViewChecked
{
  @Input({ required: true })
  formConfiguration!: FormConfiguration | null; // null is required if you use it via the async pipe (e.g formConfiguration$ | async)

  @Input()
  className = "sm:max-w-sm mx-auto";

  @Input()
  data: T | null | undefined; // null is required if you use it via the async pipe (e.g data$ | async)

  // Optional property to ensure that unique ids are being generated across the whole DOM
  @Input()
  htmlIdPrefix: string | undefined;

  @Input()
  schemaUpdateBehavior = SchemaUpdateBehavior.NON_BREAKING_CHANGES;

  /** Can be used to disable the whole form (we currently do that when the user has submitted the form) */
  @Input()
  isDisabled?: boolean;

  // The observables have been made async (thus anything which is published from this Observable will be processed in the next event loop cycle).
  // This allows us to immediately use the result from this observables and directly modify the template without any nasty workarounds such as setTimeout(0).
  // The root cause is that a parent component (e.g a page component) has already finished rendering the HTML template while a
  // child component (DataFactory) emits an event to the parent which then is trying to update the HTML template again as part of the same event loop cycle.
  @Output()
  formStatus = new EventEmitter<FormControlStatus>(true);

  @Output()
  formChange = new EventEmitter<T>(true);

  @Output()
  formSubmit = new EventEmitter<T>();

  private _valueChangesSubscription: Subscription | undefined;
  private _statusChangesSubscription: Subscription | undefined;

  private _previousFormValues?: T;

  public readonly notifications$ = new Subject<FormEvent>();

  public formName = "form";
  public allAvailableRowDefinitions: FormRow[] = [];
  public _visibleFormControls = new FormRecord({});
  public _visibleRowDefinitions: FormRow[] = [];
  public numberOfRenderers = 0;
  public previousNumberOfRenders = 0;

  private _focusedFormField: FormField | undefined;
  private _hasExpressionAppliedInThisChangeDetectionCycle = false;

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges) {
    // The schema has changed, we have to render the whole form
    if (this.hasSchemaChanged(changes)) {
      this.handleSchemaChange(changes);
    }

    // If either the schema or data have changed we would like to
    // apply the expressions
    if (this.hasSchemaOrDataChanged(changes)) {
      this.handleSchemaOrDataChange(changes);
    }

    // Check if the form has been globally disabled
    if (this.hasIsDisabledChanged(changes)) {
      this.tryApplyExpression(this._visibleFormControls.value as T);
    }

    this.resetNgOnChangeFlags();
  }

  ngAfterViewChecked() {
    if (this._focusedFormField?.key) {
      this.setFocusToFormField(this._focusedFormField);
    }
  }

  ngOnDestroy() {
    this._valueChangesSubscription?.unsubscribe();
    this._statusChangesSubscription?.unsubscribe();
  }

  trackByField(_index: number, item: FormField) {
    return item.key;
  }

  trackByFieldsRow(index: number) {
    return index;
  }

  getControlByFormField(formField: FormField) {
    return this._visibleFormControls.controls[formField.key] as FormControl;
  }

  onFocusChange(focus: FocusChangedEvent) {
    if (focus.event === "focus") {
      // The form field has gained focus
      this._focusedFormField = focus.field;
    } else {
      // The form field has lost focus
      this._focusedFormField = undefined;
    }
  }

  onFormSubmit() {
    this._visibleFormControls.markAllAsTouched();
    this.notifications$.next({ type: FormEventType.MARK_ALL_AS_TOUCHED });
    if (this._visibleFormControls.valid) {
      this.formSubmit.emit(this._visibleFormControls.value as T);
    }
  }

  hasFormUnsavedChanges() {
    // Note: dirty is a boolean, so this expression can also be false.
    // This seems like a bug with the linter rule
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return this._visibleFormControls.dirty;
  }

  getFormRowClassName(formFieldLength: number | undefined) {
    const formRowClassName = ["grid gap-2"];
    // Explicitly list classNames for tailwind to recognize
    const gridColClasses = [
      "grid-cols-1",
      "grid-cols-2",
      "grid-cols-3",
      "grid-cols-4",
    ];

    if (formFieldLength) {
      const lastEntryIndex = --formFieldLength;
      const lastEntry = gridColClasses[lastEntryIndex];

      if (!lastEntry) {
        throw new Error(
          `Could not find an entry for the formFieldLength which has been provided: '${formFieldLength.toString()}'`,
        );
      }

      formRowClassName.push(lastEntry);
    }

    return formRowClassName.join(" ");
  }

  get notificationObservable() {
    return this.notifications$.asObservable();
  }

  private resetNgOnChangeFlags() {
    this._hasExpressionAppliedInThisChangeDetectionCycle = false;
  }

  private hasSchemaOrDataChanged(changes: SimpleChanges) {
    return this.hasSchemaChanged(changes) || this.hasDataChanged(changes);
  }

  private hasIsDisabledChanged(changes: SimpleChanges) {
    const isDisabledChange = changes[getKey<FormFactory>("isDisabled")];

    if (!isDisabledChange) {
      return false;
    } else {
      return isDisabledChange.previousValue !== isDisabledChange.currentValue;
    }
  }

  private hasSchemaChanged(changes: SimpleChanges) {
    const formConfiguration = changes[getKey<FormFactory>("formConfiguration")];
    const hasOnlySchemaChanged =
      formConfiguration !== undefined && !!formConfiguration.currentValue;

    return hasOnlySchemaChanged;
  }

  private hasDataChanged(changes: SimpleChanges) {
    return changes[getKey<FormFactory>("data")];
  }

  private handleSchemaChange(changes: SimpleChanges) {
    if (!changes[getKey<FormFactory>("formConfiguration")]?.isFirstChange()) {
      this.recreateCompleteForm();
    } else {
      this.createInitialCompleteForm();
    }
  }

  private handleSchemaOrDataChange(changes: SimpleChanges) {
    const hasOnlySchemaChanged = this.hasOnlySchemaChanged(changes);

    this.handleDataChange(hasOnlySchemaChanged);
    this.tryApplyExpression(this._visibleFormControls.value as T);
    this._hasExpressionAppliedInThisChangeDetectionCycle = true;

    // We want to ensure that we only subscribe to changes AFTER the form has been initialized
    // This prevents cycles
    this.subscribeToValueChanges();
    this.subscribeToStatusChanges();
  }

  private hasOnlySchemaChanged(changes: SimpleChanges) {
    return this.hasSchemaChanged(changes) && !this.hasDataChanged(changes);
  }

  private createInitialCompleteForm() {
    if (!this.formConfiguration) {
      throw new Error(
        "It should never happen that the form is being initialized with 'null'. Please verify and adjust the checks in ngOnChanges.",
      );
    }

    this.formName = this.formConfiguration.name;
    this.allAvailableRowDefinitions = this.formConfiguration.rows;

    this.addInitialFormControls();
  }

  private resetCompleteForm() {
    this._visibleFormControls = new FormRecord({});
    this.allAvailableRowDefinitions = [];
    this._visibleRowDefinitions = [];
    this._valueChangesSubscription?.unsubscribe();
    this._statusChangesSubscription?.unsubscribe();
  }

  private handleDataChange(hasOnlySchemaChanged: boolean) {
    const newFormData = this.getNewFormData(hasOnlySchemaChanged);

    for (const key in newFormData) {
      const visibleFormControl = this._visibleFormControls.controls[key];
      if (visibleFormControl) {
        visibleFormControl.setValue(newFormData[key]);
      }
    }
  }

  /**
   * Note: Each schema change by default renders a new form. Any data from the previous form is lost unless we access the {@link _previousFormValues} property.
   * ------------------------------------
   * Scenario 1
   * ------------------------------------
   * Situation: We have a fully rendered form with a schema + data.
   * Update: We now are adding new options to a dropdown field (they were asynchronously loaded from the backend). Adding new options to a dropdown is a schema change.
   *
   * If we now only send a new schema to the data factory.
   * We then assume that we are allowed to copy the data from the existing form into the new one UNLESS the form is declared with {@link SchemaUpdateBehavior.BREAKING_CHANGES}.
   * The enum {@link SchemaUpdateBehavior.BREAKING_CHANGES} tells us that the schema changes can contain breaking changes and as a result copying data from the old to the new form is not what the developer wanted.
   *
   * ------------------------------------
   * Scenario 2
   * ------------------------------------
   * Situation: We have a fully rendered form with a schema + data.
   * Update: We now are adding new options to a dropdown field (they were asynchronously loaded from the backend) + we also provide the data (e.g what option the user had previously selected).
   *
   * Because the user provided for the schema change the corresponding data, we can then ignore the {@link _previousFormValues}.
   */
  private getNewFormData(hasOnlySchemaChanged: boolean) {
    return hasOnlySchemaChanged &&
      this.schemaUpdateBehavior === SchemaUpdateBehavior.NON_BREAKING_CHANGES
      ? this._previousFormValues
      : this.data;
  }

  private addInitialFormControls() {
    this.allAvailableRowDefinitions.forEach((formRow) => {
      formRow.items.forEach((formField) => {
        this.addFormFieldControl(formField.key, formField);
      });
    });
  }

  private tryApplyExpression(currentFormValues: T) {
    if (this._hasExpressionAppliedInThisChangeDetectionCycle) {
      return;
    }

    this._visibleRowDefinitions = this.allAvailableRowDefinitions.map((row) => {
      return {
        ...row,
        items: row.items.filter((formField) => {
          const toBeHidden = formField.expressions?.hide?.(currentFormValues);

          const toBeDisabled = this.getToBeDisabledValue(
            formField,
            currentFormValues,
          );

          const derivedValue = this.getDerivedValue(
            formField,
            currentFormValues,
            this._previousFormValues,
          );

          const visibleFormControl =
            this._visibleFormControls.controls[formField.key];

          const isHidden = !visibleFormControl;
          const isDisabled = visibleFormControl?.disabled ?? false;

          if (isHidden) {
            return this.processHiddenFormField(
              formField,
              toBeHidden,
              derivedValue,
            );
          } else {
            return this.processVisibleFormField(
              formField,
              toBeHidden,
              toBeDisabled,
              isDisabled,
              derivedValue,
            );
          }
        }),
      };
    });

    // This ensures after we have applied the expressions against the top level,
    // we propagate this information down to our children in order for them to also
    // evaluate their expressions
    // As part of this event we provide the complete form value from the form factory to
    // our children.
    //
    // Note: The initial check expression event dispatch does not reach any children,
    // as they have not been created yet
    this.dispatchCheckExpressionEvent(currentFormValues);
  }

  private getToBeDisabledValue(formField: FormField, currentFormValues: T) {
    const isFormDisabledGlobally = this.isDisabled;
    const isFormDisabledByExpression =
      formField.expressions?.disable?.(currentFormValues) ?? false;

    return isFormDisabledGlobally || isFormDisabledByExpression;
  }

  private getDerivedValue(
    formField: FormField,
    currentFormValues: T,
    previousFormValues?: T,
  ) {
    const isDeriveFnDefined = formField.expressions?.derive;

    if (isDeriveFnDefined) {
      const visibleFormControl =
        this._visibleFormControls.controls[formField.key];

      if (!visibleFormControl) {
        throw new Error(
          `Was not able to find a visible form control with the key '${formField.key}'`,
        );
      }

      const simpleDerivationContext: SimpleDerivationContext = {
        formControlType: FormControlType.SIMPLE,
        previousValue: previousFormValues,
        currentValue: currentFormValues,
        isDirty: visibleFormControl.dirty,
      };
      return formField.expressions?.derive?.(simpleDerivationContext);
    }

    return undefined;
  }

  private dispatchCheckExpressionEvent(currentFormValues: T) {
    const previousFormValue = this._previousFormValues;
    this._previousFormValues = currentFormValues;

    this.notifications$.next({
      type: FormEventType.CHECK_EXPRESSIONS,
      previousFormValues: previousFormValue,
      currentFormValues: currentFormValues,
    });
  }

  private addFormFieldControl(key: string, formField: FormFieldCreation): void {
    this._visibleFormControls.addControl(key, createFormControl(formField));
  }

  private processHiddenFormField(
    formField: FormField,
    toBeHidden: boolean | undefined,
    derivedValue: FormControlValue,
  ) {
    if (toBeHidden) {
      return false;
    } else {
      this.addFormFieldControl(formField.key, {
        initialValue: derivedValue ?? formField.initialValue,
        disabled: formField.disabled,
        required: formField.required,
        validators: formField.validators,
        asyncValidators: formField.asyncValidators,
        updateFormControlOn: formField.updateFormControlOn,
      });
      return true;
    }
  }

  private processVisibleFormField(
    formField: FormField,
    toBeHidden: boolean | undefined,
    toBeDisabled: boolean | undefined,
    isDisabled: boolean,
    derivedValue: FormControlValue,
  ) {
    if (toBeHidden) {
      this._visibleFormControls.removeControl(formField.key);
      return false;
    } else {
      const visibleFormControl =
        this._visibleFormControls.controls[formField.key];

      if (!visibleFormControl) {
        throw new Error(
          `Was not able to find a visible form control with the key '${formField.key}'`,
        );
      }
      // Handle disabled state
      if (!isDisabled && toBeDisabled) {
        visibleFormControl.disable();
      }
      if (isDisabled && toBeDisabled === false) {
        visibleFormControl.enable();
      }

      if (derivedValue) {
        visibleFormControl.setValue(derivedValue);
      }

      return true;
    }
  }

  private subscribeToValueChanges() {
    this._valueChangesSubscription =
      this._visibleFormControls.valueChanges.subscribe((values) => {
        this.tryApplyExpression(values as T);
        this.formChange.emit(values as T);
      });
  }

  private subscribeToStatusChanges() {
    this._statusChangesSubscription =
      this._visibleFormControls.statusChanges.subscribe((status) => {
        this.formStatus.emit(status);
      });
  }

  private recreateCompleteForm() {
    this.resetCompleteForm();
    this.createInitialCompleteForm();
    this.forceRerenderOfComponent();
  }

  /**
   * In case of a schema change we have to recreate the form controls and copy the values from the old form to the new one.
   * This method should ensure that the new form is focusing the same form field as before.
   */
  private setFocusToFormField(formField: FormField) {
    const lastFocusElement = document.getElementById(formField.key);
    lastFocusElement?.focus();

    // Note: This is needed in case the autocomplete feature from Chrome is being used as multiple elements are being updated in the same event cycle
    // Unfortunately there seems to be no deterministic way to find out if autocomplete was used or not ...
    this.changeDetectorRef.detectChanges();
  }

  private forceRerenderOfComponent() {
    this.numberOfRenderers++;
  }
}

export const createFormControl = ({
  initialValue,
  disabled,
  required,
  validators,
  updateFormControlOn,
  asyncValidators,
}: FormFieldCreation) =>
  new FormControl(
    {
      value: initialValue,
      disabled: !!disabled,
    },
    {
      updateOn: updateFormControlOn,
      validators: [
        // eslint-disable-next-line @typescript-eslint/unbound-method -- This method comes from Angular and does not access this. Any workaround adds unnecessary boilerplate code.
        ...(required ? [Validators.required] : []),
        ...(validators ?? []),
      ],
      asyncValidators: asyncValidators ?? null,
    },
  );

export type FormConfiguration = {
  name: string;
  rows: FormRow[];
};

export type FormRow = {
  className?: string | undefined;
  items: FormField[];
};

export enum FormEventType {
  MARK_ALL_AS_TOUCHED,
  CHECK_EXPRESSIONS,
}

export enum SchemaUpdateBehavior {
  /**
   * All schema changes are non-breaking which means we can copy the existing data from the form (what the user has already typed) into the new form.
   *
   * This is the default behavior.
   */
  NON_BREAKING_CHANGES = "nonBreakingChanges",
  /**
   * The schema updates can be breaking and thus we can not copy the existing data from the form (what the user has already typed) into the new form.
   */
  BREAKING_CHANGES = "breakingChanges",
}

interface CheckExpressionsFormEvent<T> {
  type: FormEventType.CHECK_EXPRESSIONS;
  previousFormValues?: T;
  currentFormValues: T;
}

interface MarkAllAsTouchedFormEvent {
  type: FormEventType.MARK_ALL_AS_TOUCHED;
}

export type FormFieldCreation = Pick<
  FormField,
  | "initialValue"
  | "disabled"
  | "required"
  | "validators"
  | "asyncValidators"
  | "updateFormControlOn"
>;

export type FormEvent<T = unknown> =
  | CheckExpressionsFormEvent<T>
  | MarkAllAsTouchedFormEvent;
